export class ProductMediaModal extends HTMLElement {
  constructor() {
    super();
    this.openedBy = null;
    this.previouslyFocused = null;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.syncTriggers = this.syncTriggers.bind(this);
    this.mobileQuery = window.matchMedia('(max-width: 768px)');
    this.triggers = [];

    const mediaSlides = document.querySelectorAll('media-gallery .lightbox-zoom-trigger');
    if (!mediaSlides.length) return;

    mediaSlides.forEach((slide) => {
      // The CRO gallery shows its zoom icon at every width (see
      // section-product-cro.css), so its trigger stays tappable on mobile too.
      // The regular gallery hides that icon below 769px, so it keeps the
      // desktop-only gate.
      const allowsMobile = !!slide.closest('.media-gallery--cro');
      this.triggers.push({ slide, allowsMobile });

      const openModal = () => {
        if (!allowsMobile && this.mobileQuery.matches) return;
        const modal = document.querySelector(slide.getAttribute('data-modal'));
        if (modal) modal.showModal(slide);
      };

      slide.addEventListener('click', openModal);
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openModal();
      });
    });

    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) {
      closeButton.addEventListener('click', this.hideModal.bind(this));
    }

    this.addEventListener('pointerup', (event) => {
      if (event.pointerType === 'mouse') this.hideModal();
    });
  }

  connectedCallback() {
    this.syncTriggers();
    this.mobileQuery.addEventListener('change', this.syncTriggers);
  }

  disconnectedCallback() {
    this.mobileQuery.removeEventListener('change', this.syncTriggers);
  }

  /**
   * Non-button triggers get a focus target and button role only while they can
   * open the modal, re-checked whenever the 768px breakpoint is crossed. Only
   * attributes added here are removed, never ones the markup set.
   */
  syncTriggers() {
    for (const { slide, allowsMobile } of this.triggers) {
      if (slide instanceof HTMLButtonElement) continue;

      const operable = allowsMobile || !this.mobileQuery.matches;

      if (operable && !slide.hasAttribute('tabindex') && !slide.hasAttribute('role')) {
        slide.setAttribute('tabindex', '0');
        slide.setAttribute('role', 'button');
        slide.dataset.modalTriggerManaged = 'true';
      } else if (!operable && slide.dataset.modalTriggerManaged === 'true') {
        slide.removeAttribute('tabindex');
        slide.removeAttribute('role');
        delete slide.dataset.modalTriggerManaged;
      }
    }
  }

  _checkElementVisibility(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') return false;
    if (element.classList.contains('hidden') || element.classList.contains('visually-hidden')) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
      return false;
    }

    const rects = element.getClientRects();
    return rects.length > 0 && (rects[0].width > 0 || rects[0].height > 0);
  }

  getFocusableElements() {
    return Array.from(
      this.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => this._checkElementVisibility(el));
  }

  getFocusFallback() {
    const dialog = this.querySelector('[role="dialog"]') || this.querySelector('.product-media-modal__dialog') || this;
    if (dialog instanceof HTMLElement && !dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }
    return dialog;
  }

  /**
   * Keep focus inside the open modal (Tab cycle + empty-focusable fallback).
   */
  trapFocus(event) {
    const focusable = this.getFocusableElements();

    // No visible focusable controls: keep focus on the dialog container so it is not lost.
    if (!focusable.length) {
      event.preventDefault();
      const fallback = this.getFocusFallback();
      if (fallback instanceof HTMLElement) {
        fallback.focus({ preventScroll: true });
      }
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!this.contains(active)) {
      event.preventDefault();
      first.focus({ preventScroll: true });
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  handleKeydown(event) {
    if (!this.hasAttribute('open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hideModal();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  handleFocusIn(event) {
    if (!this.hasAttribute('open')) return;
    if (this.contains(event.target)) return;

    // If focus escapes the modal, pull it back to a visible control or the dialog.
    const focusable = this.getFocusableElements();
    const fallback = focusable[0] || this.querySelector('[id^="ModalClose-"]') || this.getFocusFallback();
    if (fallback instanceof HTMLElement) {
      fallback.focus({ preventScroll: true });
    }
  }

  showModal(opener) {
    this.openedBy = opener;
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : opener;

    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    // Dialog / aria-modal semantics live on the inner `.product-media-modal__dialog` in Liquid.
    this.showActiveMedia();

    document.addEventListener('keydown', this.handleKeydown, true);
    document.addEventListener('focusin', this.handleFocusIn, true);

    // Move focus into the modal and keep it trapped until close.
    requestAnimationFrame(() => {
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');

      const closeButton = this.querySelector('[id^="ModalClose-"]');
      const focusable = this.getFocusableElements();
      const focusTarget = closeButton || focusable[0] || this.getFocusFallback();

      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
      }
    });
  }

  hideModal() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');

    document.removeEventListener('keydown', this.handleKeydown, true);
    document.removeEventListener('focusin', this.handleFocusIn, true);

    const restoreTarget =
      (this.openedBy instanceof HTMLElement && this.openedBy) ||
      (this.previouslyFocused instanceof HTMLElement && this.previouslyFocused);

    // A microtask later: the modal background guard releases the page (the
    // trigger included) in its own mutation microtask, queued by the
    // removeAttribute above, and an inert trigger cannot take focus.
    if (restoreTarget) {
      queueMicrotask(() => restoreTarget.focus({ preventScroll: true }));
    }

    this.openedBy = null;
    this.previouslyFocused = null;
  }

  showActiveMedia() {
    if (!(this.openedBy instanceof HTMLElement)) return;

    this.querySelectorAll(
      `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
    ).forEach((element) => {
      element.classList.remove('active');
    });
    const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
    if (!activeMedia) return;
    activeMedia.classList.add('active');
    activeMedia.scrollIntoView();
  }
}

if (!customElements.get('product-media-modal')) {
  customElements.define('product-media-modal', ProductMediaModal);
}
